/**
 * Copyright 2026 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * PDF Export Utilities for User Stories
 * 
 * Provides functions to generate PDF files from user stories and epics.
 * Uses jspdf for PDF generation.
 */

import { jsPDF } from 'jspdf';
import type { UserStory, Epic } from '../types';
import { normalizeAcceptanceCriteria } from '@shared/types/user-stories';

export interface PdfExportOptions {
  title?: string;
  subtitle?: string;
  includeTableOfContents?: boolean;
  pageSize?: 'a4' | 'letter';
  orientation?: 'portrait' | 'landscape';
  includeStatus?: boolean;
  includeAcceptanceCriteria?: boolean;
  productName?: string;
}

const DEFAULT_OPTIONS: Required<PdfExportOptions> = {
  title: 'User Stories',
  subtitle: '',
  includeTableOfContents: true,
  pageSize: 'a4',
  orientation: 'portrait',
  includeStatus: true,
  includeAcceptanceCriteria: true,
  productName: '',
};

interface PdfColors {
  primary: [number, number, number];
  secondary: [number, number, number];
  accent: [number, number, number];
  text: [number, number, number];
  lightGray: [number, number, number];
  success: [number, number, number];
  warning: [number, number, number];
  error: [number, number, number];
}

const COLORS: PdfColors = {
  primary: [0, 102, 204],      // Blue
  secondary: [100, 100, 100],  // Dark gray
  accent: [124, 58, 237],      // Purple
  text: [33, 33, 33],          // Near black
  lightGray: [245, 245, 245],  // Light background
  success: [34, 197, 94],      // Green
  warning: [234, 179, 8],      // Yellow
  error: [239, 68, 68],        // Red
};

function getStatusColor(status: string): [number, number, number] {
  switch (status) {
    case 'Completed':
      return COLORS.success;
    case 'In Progress':
      return COLORS.primary;
    case 'Blocked':
      return COLORS.error;
    case 'On Hold':
      return COLORS.warning;
    case 'In Review':
    case 'Ready For Review':
      return COLORS.accent;
    default:
      return COLORS.secondary;
  }
}

function formatDate(): string {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

class PdfGenerator {
  private doc: jsPDF;
  private currentY: number = 20;
  private pageWidth: number;
  private pageHeight: number;
  private marginLeft: number = 20;
  private marginRight: number = 20;
  private marginTop: number = 20;
  private marginBottom: number = 25;
  private contentWidth: number;
  private pageNumber: number = 1;
  private options: Required<PdfExportOptions>;

  constructor(options: PdfExportOptions = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.doc = new jsPDF({
      orientation: this.options.orientation,
      unit: 'mm',
      format: this.options.pageSize,
    });
    
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - this.marginLeft - this.marginRight;
  }

  private checkPageBreak(requiredSpace: number = 30): void {
    if (this.currentY + requiredSpace > this.pageHeight - this.marginBottom) {
      this.addPage();
    }
  }

  private addPage(): void {
    this.doc.addPage();
    this.pageNumber++;
    this.currentY = this.marginTop;
    this.addFooter();
  }

  private addFooter(): void {
    const footerY = this.pageHeight - 10;
    this.doc.setFontSize(9);
    this.doc.setTextColor(...COLORS.secondary);
    
    // Page number on right
    this.doc.text(
      `Page ${this.pageNumber}`,
      this.pageWidth - this.marginRight,
      footerY,
      { align: 'right' }
    );
    
    // Date on left
    this.doc.text(
      `Generated: ${formatDate()}`,
      this.marginLeft,
      footerY,
      { align: 'left' }
    );
  }

  private addTitle(): void {
    // Header background
    this.doc.setFillColor(...COLORS.primary);
    this.doc.rect(0, 0, this.pageWidth, 45, 'F');
    
    // Title
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(24);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(this.options.title, this.marginLeft, 22);
    
    // Subtitle / Product name
    if (this.options.productName || this.options.subtitle) {
      this.doc.setFontSize(12);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(
        this.options.productName || this.options.subtitle,
        this.marginLeft,
        32
      );
    }
    
    // Date on right
    this.doc.setFontSize(10);
    this.doc.text(formatDate(), this.pageWidth - this.marginRight, 32, { align: 'right' });
    
    this.currentY = 55;
    this.addFooter();
  }

  private addTableOfContents(epics: Epic[]): void {
    this.checkPageBreak(40);
    
    this.doc.setTextColor(...COLORS.text);
    this.doc.setFontSize(16);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Table of Contents', this.marginLeft, this.currentY);
    this.currentY += 10;
    
    this.doc.setFontSize(11);
    this.doc.setFont('helvetica', 'normal');
    
    for (const epic of epics) {
      this.checkPageBreak(8);
      
      // Epic ID in accent color
      this.doc.setTextColor(...COLORS.accent);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(epic.id, this.marginLeft, this.currentY);
      
      // Epic title
      this.doc.setTextColor(...COLORS.text);
      this.doc.setFont('helvetica', 'normal');
      const titleX = this.marginLeft + 30;
      const maxTitleWidth = this.contentWidth - 30;
      const titleLines = this.doc.splitTextToSize(epic.title, maxTitleWidth);
      this.doc.text(titleLines[0], titleX, this.currentY);
      
      this.currentY += 7;
    }
    
    this.currentY += 10;
  }

  private addEpic(epic: Epic, stories: UserStory[]): void {
    this.checkPageBreak(50);
    
    // Epic header background
    this.doc.setFillColor(...COLORS.lightGray);
    this.doc.roundedRect(this.marginLeft, this.currentY - 5, this.contentWidth, 20, 3, 3, 'F');
    
    // Epic ID badge
    this.doc.setFillColor(...COLORS.accent);
    this.doc.roundedRect(this.marginLeft + 3, this.currentY - 2, 40, 14, 2, 2, 'F');
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(epic.id, this.marginLeft + 5, this.currentY + 7);
    
    // Epic title
    this.doc.setTextColor(...COLORS.text);
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    const titleX = this.marginLeft + 48;
    const maxTitleWidth = this.contentWidth - 50;
    const titleLines = this.doc.splitTextToSize(epic.title, maxTitleWidth);
    this.doc.text(titleLines, titleX, this.currentY + 7);
    
    this.currentY += 20;
    
    // Epic description
    if (epic.description) {
      this.doc.setTextColor(...COLORS.secondary);
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'italic');
      const descLines = this.doc.splitTextToSize(epic.description, this.contentWidth - 10);
      this.doc.text(descLines, this.marginLeft + 5, this.currentY);
      this.currentY += descLines.length * 5 + 5;
    }
    
    // Stories
    for (const story of stories) {
      this.addStory(story);
    }
    
    this.currentY += 10;
  }

  private addStory(story: UserStory): void {
    // First, calculate all content dimensions to determine dynamic card height
    const cardPadding = 8;
    const lineHeightTitle = 5;
    const lineHeightBody = 4;
    const lineHeightCriteria = 4;
    
    // Calculate title lines
    this.doc.setFontSize(11);
    const maxStoryTitleWidth = this.contentWidth - 90;
    const storyTitleLines = this.doc.splitTextToSize(story.title, maxStoryTitleWidth);
    const titleHeight = Math.max(storyTitleLines.length * lineHeightTitle, 10);
    
    // Calculate story text lines
    this.doc.setFontSize(9);
    const role = story.role || 'user';
    const article = ['a', 'e', 'i', 'o', 'u'].includes(role[0]?.toLowerCase()) ? 'an' : 'a';
    const storyText = `As ${article} ${role}, I want to ${story.goal || story.title}, so that ${story.benefit || 'I can achieve my goals'}.`;
    const storyLines = this.doc.splitTextToSize(storyText, this.contentWidth - 25);
    const storyTextHeight = storyLines.length * lineHeightBody + 3;
    
    // Calculate acceptance criteria height
    let acceptanceCriteriaHeight = 0;
    const criteriaLinesArray: string[][] = [];
    const normalizedCriteria = normalizeAcceptanceCriteria(story.acceptanceCriteria);
    if (this.options.includeAcceptanceCriteria && normalizedCriteria.length > 0) {
      acceptanceCriteriaHeight += 8; // Header + spacing
      for (const criterion of normalizedCriteria) {
        const criteriaText = criterion.release 
          ? `${criterion.text} [${criterion.release}]` 
          : criterion.text;
        const criteriaLines = this.doc.splitTextToSize(criteriaText, this.contentWidth - 30);
        criteriaLinesArray.push(criteriaLines);
        acceptanceCriteriaHeight += criteriaLines.length * lineHeightCriteria + 2;
      }
    }
    
    // Total card height
    const totalCardHeight = cardPadding + titleHeight + storyTextHeight + acceptanceCriteriaHeight + cardPadding;
    
    // Check if we need a page break for this card
    this.checkPageBreak(totalCardHeight + 5);
    
    const cardStartY = this.currentY - 3;
    
    // Draw the story container with dynamic height
    this.doc.setDrawColor(...COLORS.lightGray);
    this.doc.setLineWidth(0.5);
    this.doc.roundedRect(
      this.marginLeft + 5,
      cardStartY,
      this.contentWidth - 10,
      totalCardHeight,
      2,
      2,
      'S'
    );
    
    // Story ID
    this.doc.setTextColor(...COLORS.primary);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(story.id, this.marginLeft + 10, this.currentY + 5);
    
    // Story title (can be multi-line)
    this.doc.setTextColor(...COLORS.text);
    this.doc.setFontSize(11);
    const storyTitleX = this.marginLeft + 45;
    this.doc.text(storyTitleLines, storyTitleX, this.currentY + 5);
    
    // Status badge
    if (this.options.includeStatus && story.status) {
      const statusColor = getStatusColor(story.status);
      const statusX = this.pageWidth - this.marginRight - 35;
      this.doc.setFillColor(...statusColor);
      this.doc.roundedRect(statusX, this.currentY, 25, 10, 2, 2, 'F');
      this.doc.setTextColor(255, 255, 255);
      this.doc.setFontSize(7);
      this.doc.setFont('helvetica', 'bold');
      
      const statusText = story.status.length > 10 
        ? story.status.substring(0, 8) + '..'
        : story.status;
      this.doc.text(statusText, statusX + 12.5, this.currentY + 6.5, { align: 'center' });
    }
    
    this.currentY += titleHeight + 2;
    
    // User story format
    this.doc.setTextColor(...COLORS.secondary);
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(storyLines, this.marginLeft + 10, this.currentY);
    this.currentY += storyLines.length * lineHeightBody + 3;
    
    // Acceptance criteria
    if (this.options.includeAcceptanceCriteria && normalizedCriteria.length > 0) {
      this.doc.setTextColor(...COLORS.text);
      this.doc.setFontSize(9);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Acceptance Criteria:', this.marginLeft + 10, this.currentY);
      this.currentY += 5;
      
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(...COLORS.secondary);
      
      for (let i = 0; i < normalizedCriteria.length; i++) {
        const criteriaLines = criteriaLinesArray[i];
        const bulletX = this.marginLeft + 12;
        const textX = this.marginLeft + 16;
        
        // Bullet point
        this.doc.setFillColor(...COLORS.primary);
        this.doc.circle(bulletX, this.currentY - 1, 0.8, 'F');
        
        // Criteria text
        this.doc.text(criteriaLines, textX, this.currentY);
        this.currentY += criteriaLines.length * lineHeightCriteria + 2;
      }
    }
    
    // Set Y position to after the card (ensures no overlap with next card)
    const cardEndY = cardStartY + totalCardHeight;
    const spacing = 5;
    this.currentY = cardEndY + spacing;
  }

  private addSummary(epicCount: number, storyCount: number): void {
    this.checkPageBreak(30);
    
    this.doc.setFillColor(...COLORS.lightGray);
    this.doc.roundedRect(this.marginLeft, this.currentY, this.contentWidth, 25, 3, 3, 'F');
    
    this.doc.setTextColor(...COLORS.text);
    this.doc.setFontSize(12);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Summary', this.marginLeft + 10, this.currentY + 10);
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(
      `Total Epics: ${epicCount}  |  Total User Stories: ${storyCount}`,
      this.marginLeft + 10,
      this.currentY + 18
    );
    
    this.currentY += 35;
  }

  public generate(
    epics: Epic[],
    stories: UserStory[],
    selectedEpicIds: Set<string>,
    selectedStoryIds: Set<string>
  ): jsPDF {
    // Filter selected items
    const selectedEpics = epics.filter(e => selectedEpicIds.has(e.id));
    const selectedStories = stories.filter(s => selectedStoryIds.has(s.id));
    
    // Group stories by epic
    const storiesByEpic = new Map<string, UserStory[]>();
    for (const story of selectedStories) {
      const epicId = story.epicId || '__no_epic__';
      if (!storiesByEpic.has(epicId)) {
        storiesByEpic.set(epicId, []);
      }
      storiesByEpic.get(epicId)!.push(story);
    }
    
    // Add title page
    this.addTitle();
    
    // Add table of contents if enabled
    if (this.options.includeTableOfContents && selectedEpics.length > 0) {
      this.addTableOfContents(selectedEpics);
    }
    
    // Add summary
    this.addSummary(selectedEpics.length, selectedStories.length);
    
    // Add epics and their stories
    for (const epic of selectedEpics) {
      const epicStories = storiesByEpic.get(epic.id) || [];
      this.addEpic(epic, epicStories);
    }
    
    // Add orphan stories (stories without epics or with unselected epics)
    const orphanStories = selectedStories.filter(s => {
      return !s.epicId || !selectedEpicIds.has(s.epicId);
    });
    
    if (orphanStories.length > 0) {
      this.checkPageBreak(40);
      
      // Section header for orphan stories
      this.doc.setFillColor(...COLORS.lightGray);
      this.doc.roundedRect(this.marginLeft, this.currentY - 5, this.contentWidth, 15, 3, 3, 'F');
      
      this.doc.setTextColor(...COLORS.text);
      this.doc.setFontSize(14);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Additional Stories', this.marginLeft + 10, this.currentY + 5);
      this.currentY += 18;
      
      for (const story of orphanStories) {
        this.addStory(story);
      }
    }
    
    return this.doc;
  }

  public save(filename: string): void {
    this.doc.save(filename);
  }
}

/**
 * Generate a timestamped filename for the PDF export
 */
export function generatePdfFilename(prefix: string = 'user-stories'): string {
  const timestamp = new Date().toISOString().split('T')[0];
  return `${prefix}-${timestamp}.pdf`;
}

/**
 * Export selected epics and user stories to PDF
 */
export function exportToPdf(
  epics: Epic[],
  stories: UserStory[],
  selectedEpicIds: Set<string>,
  selectedStoryIds: Set<string>,
  options: PdfExportOptions = {}
): { filename: string; epicCount: number; storyCount: number } {
  const generator = new PdfGenerator(options);
  const pdf = generator.generate(epics, stories, selectedEpicIds, selectedStoryIds);
  
  const filename = generatePdfFilename(
    options.productName ? `${options.productName}-user-stories` : 'user-stories'
  );
  
  pdf.save(filename);
  
  return {
    filename,
    epicCount: selectedEpicIds.size,
    storyCount: selectedStoryIds.size,
  };
}

export { PdfGenerator };
